import './kyb.scss'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Fab,
  FormControlLabel,
  Link,
  Modal,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2/Grid2'
import { Accordion, AccordionDetails, AccordionSummary } from '../../components/common/Accordion/Accordion'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab'
import { addAdditionalParams, getCountrySegment, removeLastWord } from './kyb.hook-helpers'
import {
  useCheckKybMutation,
  useCreateNewCommentMutation,
  useDeleteFilePersonMutation,
  useFindAvailableUsersQuery,
  useFindKybPersonByIdQuery,
  useFindUserMasterQuery,
  useUpdateCheckMutation,
  useUploadFilePersonMutation,
  useUpdateKybPersonMutation,
  useRestartKybSubjectMutation,
  useRunPersonRiskMatrixMutation,
  useUpdateSubjectRiskLevelMutation,
  useUpdateSubjectIsPrivateMutation,
} from '../../services/guenoApi'
import KybModal from './components/KybModal'
import KybTitle from './components/KybTitle'
import { buildTimeStringFromTimestamp } from '../../components/common/time/timeHelper'
import { StatusIndicator } from '../../components/utilities/StatusIndicator'
import KybNewCommentModal from './components/KybNewCommentModal'
import KybEditCheckStatusModal from './components/KybEditCheckStatusModal'
import KybEditStatusModal from './components/KybEditStatusModal'
import KybToPdf from './components/KybToPdf'
import { colorsByStatus, iconByCountry } from './constants'
import KybChecksDefaultOrder from './components/KybChecksDefaultOrder.json'
import KybChecks from './components/KybChecks.json'
import KybCheckDetail from './components/KybCheckDetail'
import { KybUtils } from './kyb.utils'
import KybPreviousChecks from './components/KybPreviousChecks'
import { camelCaseFormatter } from '../../components/utilities/formatters/camelCase'
import toaster from '../../toaster'
import { FileUploader } from 'react-drag-drop-files'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TabComponent from './components/TabComponent'
import CustomNoRowsOverlay from '../../components/common/datagrid/CustomNoRowsOverlay'
import DatatableAssignUsers from '../../components/common/datatable/DatatableAssignedUsers'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { Status } from '../../components/utilities/Status'
import ProcessingSpinner from '../../components/common/alertFetchSpinner/processingSpinner'
import KybResetModal from './components/KybResetModal'
import { RiskLevelSlider } from '../../components/utilities/RiskLevelSlider'
import TimelineTable from './components/TimelineTable'
import { useGetLastRiskHistoryQuery, useLazyGetRiskHistoryQuery } from '../../features/riskMatrix/riskMatrixSlice'
import RiskMatrixResultModal from './components/RiskMatrixResultModal'
import CNJNegativeCertificate from '../../components/shared/CNJNegativeCertificate'
import KybPersonRemoveManualAddress from './components/KybPersonRemoveManualAddress'
import KybPersonEditManualAddress from './components/KybPersonEditManualAddress'
import KybPersonRemoveManualEmail from './components/KybPersonRemoveManualEmail'
import KybEditFieldModal from './components/KybEditFieldModal'
import KybPersonRemoveManualPhoneNumber from './components/KybPersonRemoveManualPhoneNumber'
import KybPersonEditManualPhoneNumber from './components/KybPersonEditManualPhoneNumber'
import KybIgnoreRiskMatrixReason from './components/KybIgnoreRiskMatrixReason'
import KybEditAssignedUsersReason from './components/KybEditAssignedUsersReason'
import KybEditRiskLevelModal from './components/KybEditRiskLevelModal'

const KybDetailPerson = () => {
  const { id, country } = useParams()
  const currentCountry = country.toUpperCase()
  const { t } = useTranslation(['common', 'messages'])
  const { emailClient, email, activeServices, roles } = useSelector((state) => state.login)
  const languageState = useSelector((state) => state.language.lang)
  const { theme } = useSelector((state) => state.theme)
  const [selectedTab, setSelectedTab] = useState('checks')
  const [editStatusModal, setEditStatusModal] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [modalNewCommentOpen, setModalNewCommentOpen] = useState(false)
  const [checkId, setCheckId] = useState('')
  const [checkStatus, setCheckStatus] = useState('')
  const [checkType, setCheckType] = useState('')
  const [openModalCheckStatus, setOpenModalCheckStatus] = useState(false)
  const [isEditingAssign, setIsEditingAssign] = useState(false)
  const [modalEditAssign, setModalEditAssign] = useState(false)
  const [assignsToSave, setAssignsToSave] = useState([])
  const [openModalKybReset, setOpenModalKybReset] = useState(false)
  const [addressModalOpen, setAddressModalOpen] = useState(false)
  const [editAddressModalOpen, setEditAddressModalOpen] = useState(false)
  const [removeAddressModalOpen, setRemoveAddressModalOpen] = useState(false)
  const [editEmailModalOpen, setEditEmailModalOpen] = useState(false)
  const [removeEmailModalOpen, setRemoveEmailModalOpen] = useState(false)
  const [editPhoneNumberModalOpen, setEditPhoneNumberModalOpen] = useState(false)
  const [removePhoneNumberModalOpen, setRemovePhoneNumberModalOpen] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [phoneNumberModalOpen, setPhoneNumberModalOpen] = useState(false)
  const [modalIgnoreRiskMatrix, setModalIgnoreRiskMatrix] = useState(false)
  const [modalEditRiskLevel, setModalEditRiskLevel] = useState(false)
  const [runRiskMatrix, { isLoading: isRunningRiskMatrix }] = useRunPersonRiskMatrixMutation()
  const [openModalRiskResult, setOpenModalRiskResult] = useState(false)
  const [coordinates, setCoordinates] = useState(null)

  const countrySegment = useMemo(() => getCountrySegment(currentCountry), [currentCountry])
  const { data, isFetching, isLoading, refetch } = useFindKybPersonByIdQuery({
    id,
    languageState,
    countrySegment,
  })
  const kybPerson = data?.data

  const [restartKyb] = useRestartKybSubjectMutation({ fixedCacheKey: 'subject-reset' })
  const [updateKybPerson] = useUpdateKybPersonMutation({ fixedCacheKey: 'update-subject' })
  const [createCheck, { isLoading: createCheckLoading, originalArgs: createCheckArgs }] = useCheckKybMutation()
  const [createNewComment, { isLoading: createNewCommentLoading }] = useCreateNewCommentMutation()
  const [updateRiskLevel] = useUpdateSubjectRiskLevelMutation({ fixedCacheKey: 'kyb-person-update-risk-level' })
  const [updateCheck] = useUpdateCheckMutation()
  const [updateSubjectIsPrivate] = useUpdateSubjectIsPrivateMutation()
  const [uploadFile, { isLoading: isProcessingFile }] = useUploadFilePersonMutation()
  const [deleteFile, { isLoading: isProcessingFileDelete }] = useDeleteFilePersonMutation()

  const { data: dataForAvailableUsers } = useFindAvailableUsersQuery()
  const availableUsers = useMemo(() => dataForAvailableUsers?.data || [], [dataForAvailableUsers])
  const { data: userMasters } = useFindUserMasterQuery({ emailClient })

  const kycId = useMemo(() => kybPerson?.kym?.kyc ?? '', [kybPerson?.kym])
  const kycV2Id = useMemo(() => kybPerson?.kym?.kycv2 ?? '', [kybPerson?.kym?.kycv2])
  const kytId = useMemo(() => kybPerson?.kym?.kyt ?? '', [kybPerson?.kym?.kyt])

  const [riskHistory, setRiskHistory] = useState(null)

  const { data: lastRiskHistoryData, refetch: getLastRiskHistory } = useGetLastRiskHistoryQuery(
    {
      kybId: kybPerson?._id,
      kybType: 'files_person',
      kybCountry: currentCountry,
    },
    { skip: !kybPerson },
    { fixedCacheKey: 'get-last-risk-history' },
  )

  const [getRiskHistory] = useLazyGetRiskHistoryQuery({ fixedCacheKey: 'get-risk-history' })

  useEffect(() => {
    if (lastRiskHistoryData?.data) {
      setRiskHistory(lastRiskHistoryData?.data)
    }
  }, [lastRiskHistoryData])

  const handleFetchHistory = async (id) => {
    if (!id) return

    const { data } = await getRiskHistory({
      id,
      kybId: kybPerson?._id,
      kybCountry: currentCountry,
    })

    if (data?.data) {
      setRiskHistory(data.data)
    }
  }

  const handleFetchLastHistory = async () => {
    const { data } = await getLastRiskHistory()
    if (data?.data) {
      setRiskHistory(data.data)
    }
  }

  const nationalityIconUrl = useMemo(() => {
    const nationality = (kybPerson?.nationality || '').toLowerCase()
    if (!nationality) return null

    return `https://flagcdn.com/16x12/${iconByCountry[nationality] || nationality}.png`
  }, [kybPerson?.nationality])

  const CustomTabList = styled(TabList)({
    borderBottom: '1px solid #1C282026',
    '& .MuiTabs-flexContainer': {
      justifyContent: 'center',
    },
    '& ::-webkit-scrollbar-thumb': {
      background: theme === 'light' ? '#c9d2cc' : '#57635b',
    },
  })

  const checks = useMemo(() => {
    if (!currentCountry) return []

    let sortedList = KybChecksDefaultOrder[currentCountry]['PERSON']

    if (userMasters?.kybChecks?.[currentCountry]?.['PERSON'])
      sortedList = userMasters.kybChecks?.[currentCountry]?.['PERSON']

    return KybChecks[currentCountry]['PERSON']
      ?.map((section) => ({
        ...section,
        items: section.items?.filter((check) => {
          // Filter hidden checks (priority check)
          if (KybUtils.isCheckHidden(check.key)) {
            return false
          }
          // Filter JusBrasil check based on email restrictions
          if (KybUtils.isJapJusCheck(check.name) && !KybUtils.canViewCheck(check.name, emailClient, 'jap_jus')) {
            return false
          }
          // Filter AMLDD checks based on email restrictions
          if (KybUtils.isAmlddCheck(check.name) && !KybUtils.canViewCheck(check.name, emailClient, 'amldd')) {
            return false
          }
          return true
        }),
      }))
      ?.filter((section) => section.items?.length > 0)
      ?.sort((a, b) => sortedList.indexOf(a.title) - sortedList.indexOf(b.title))
  }, [userMasters?.kybChecks, currentCountry, emailClient])

  const manualAddress = useMemo(() => {
    return kybPerson?.addresses?.find((addr) => addr.type === 'MANUAL')
  }, [kybPerson?.addresses])

  const finalAddress = useMemo(() => {
    if (Array.isArray(kybPerson?.addresses) && kybPerson.addresses.length) {
      const aux =
        kybPerson.addresses.find((addr) => addr.type === 'MANUAL') ||
        kybPerson.addresses.find((addr) => addr.type === 'PRINCIPAL') ||
        kybPerson.addresses[0]

      if (typeof aux === 'object' && aux !== null) {
        if (country.toUpperCase() === 'CR') {
          const parts = [aux.district, aux.city, aux.state, aux.postcode, aux.country].filter(Boolean)
          return parts.length > 0 ? parts.join(', ') : '---'
        }

        const parts = [aux.street, aux.number, aux.city, aux.state, aux.postcode, aux.country].filter(Boolean)
        return parts.length > 0 ? parts.join(', ') : '---'
      }

      return aux
    }

    return '---'
  }, [kybPerson?.addresses, country])

  const finalEmail = useMemo(() => {
    if (!Array.isArray(kybPerson?.emailList)) {
      return ''
    }
    const finalEmailResult = kybPerson?.emailList.find((addr) => addr.type === 'MANUAL')
    return finalEmailResult?.EmailAddress || ''
  }, [kybPerson?.emailList, country])

  const allAddresses = useMemo(() => {
    if (!Array.isArray(kybPerson?.addresses)) return []

    const sortedAddresses =
      country.toUpperCase() === 'BR'
        ? (() => {
            const manual = kybPerson.addresses.find((addr) => addr.type === 'MANUAL')
            const rest = kybPerson.addresses.filter((addr) => addr !== manual)

            const sortedRest = [...rest].sort((a, b) => {
              const dateA = a.lastPassageDate ? new Date(a.lastPassageDate).getTime() : 0
              const dateB = b.lastPassageDate ? new Date(b.lastPassageDate).getTime() : 0
              return dateB - dateA
            })

            return manual ? [manual, ...sortedRest] : sortedRest
          })()
        : kybPerson.addresses

    return sortedAddresses.map((addr, _) => {
      let formatted
      if (country.toUpperCase() === 'CR') {
        const parts = [addr.district, addr.city, addr.state, addr.postcode, addr.country].filter(Boolean)
        formatted = parts.length > 0 ? parts.join(', ') : '---'
      } else {
        const parts = [addr.street, addr.number, addr.city, addr.state, addr.postcode, addr.country].filter(Boolean)
        formatted = parts.length > 0 ? parts.join(', ') : '---'
      }

      const passageDate =
        country.toUpperCase() === 'BR'
          ? addr.lastPassageDate
            ? new Date(addr.lastPassageDate).toLocaleDateString()
            : '---'
          : undefined

      return {
        label:
          addr.type === 'MANUAL'
            ? t('common:manual')
            : addr.type === 'PRINCIPAL'
            ? t('common:main')
            : t(`common:${addr.type}`),
        formatted,
        passageDate,
      }
    })
  }, [kybPerson?.addresses, country, t])

  const emailList = useMemo(() => {
    if (!kybPerson?.emailList) return []

    const sortedEmailList =
      country.toUpperCase() === 'BR'
        ? [...kybPerson.emailList].sort((a, b) => {
            const dateA = a.EntityLastPassageDate ? new Date(a.EntityLastPassageDate).getTime() : 0
            const dateB = b.EntityLastPassageDate ? new Date(b.EntityLastPassageDate).getTime() : 0
            return dateB - dateA
          })
        : kybPerson.emailList

    return sortedEmailList.map((emailDetail) => {
      const passageDate =
        country.toUpperCase() === 'BR'
          ? emailDetail.EntityLastPassageDate
            ? new Date(emailDetail.EntityLastPassageDate).toLocaleDateString()
            : '---'
          : undefined

      const label =
        emailDetail.type === 'MANUAL'
          ? t('common:manual')
          : emailDetail.type === 'PRINCIPAL'
          ? t('common:main')
          : t(`common:${emailDetail.Type}`)

      return {
        label,
        email: emailDetail.EmailAddress || '---',
        passageDate,
      }
    })
  }, [kybPerson?.emailList, country, t])

  const manualEmail = useMemo(() => {
    return kybPerson?.emailList.find((e) => e.type === 'MANUAL')
  }, [kybPerson?.emailList])

  const manualPhoneNumber = useMemo(() => {
    return kybPerson?.phoneNumberList?.find((phone) => phone.Type === 'MANUAL')
  }, [kybPerson?.phoneNumberList])

  const phoneNumberList = useMemo(() => {
    if (!kybPerson?.phoneNumberList) return []

    const manual = kybPerson.phoneNumberList.find((phone) => phone.Type === 'MANUAL')
    const rest = kybPerson.phoneNumberList.filter((phone) => phone !== manual)

    const sortedRest =
      country.toUpperCase() === 'BR'
        ? [...rest].sort((a, b) => {
            const dateA = a.EntityLastPassageDate ? new Date(a.EntityLastPassageDate).getTime() : 0
            const dateB = b.EntityLastPassageDate ? new Date(b.EntityLastPassageDate).getTime() : 0
            return dateB - dateA
          })
        : rest

    const orderedList = manual ? [manual, ...sortedRest] : sortedRest

    function getPhoneNumber(phoneNumberData) {
      if (!phoneNumberData) {
        return ''
      }
      return `${phoneNumberData.CountryCode || ''}${phoneNumberData.AreaCode || ''}${phoneNumberData.Number || ''}`
    }

    return orderedList.map((phoneNumberDetail) => {
      const phoneNumber = getPhoneNumber(phoneNumberDetail)

      const passageDate =
        country.toUpperCase() === 'BR'
          ? phoneNumberDetail.EntityLastPassageDate
            ? new Date(phoneNumberDetail.EntityLastPassageDate).toLocaleDateString()
            : '---'
          : undefined

      return {
        label:
          phoneNumberDetail.Type === 'MANUAL'
            ? t('common:manual')
            : phoneNumberDetail.Type === 'PRINCIPAL'
            ? t('common:main')
            : t('common:phone'),
        phoneNumber: phoneNumber || '---',
        passageDate,
      }
    })
  }, [kybPerson?.phoneNumberList, country, t])

  const [expandedMain, setExpandedMain] = useState([false, false, false, false, false, false])
  const [expanded, setExpanded] = useState({})

  const handleRestart = async () => {
    try {
      await restartKyb({ countrySegment, id }).unwrap()
      toaster.success(t('messages:restartKybSuccess'))
      setOpenModalKybReset(false)
    } catch (error) {
      toaster.error(t('messages:restartKybFailure'))
      console.error(error?.data?.data)
    }
  }

  const handleChangeExpandedMain = (indexMain) => {
    const newState = { ...expandedMain }
    newState[indexMain] = !newState[indexMain]
    setExpandedMain(newState)
  }

  const handleChangeExpanded = (indexMain, index) => {
    const key = `${indexMain}-${index}`
    const newState = { ...expanded, [key]: !expanded[key] }
    setExpanded(newState)
  }

  const handleFetch = () => {
    setOpenModalCheckStatus(false)
    setModalNewCommentOpen(false)
    setEditStatusModal(false)
  }

  const handleCreateCheck = async (e, type) => {
    e.preventDefault()
    const requiresNameAndLast = type === 'procesos-judiciales-co' || type === 'fiscalia-co' || type === 'secop-co'
    const requiresOnlyLastName = type === 'inpec-co' || type === 'experian-hcpn-co'

    const splitName = kybPerson?.name ? kybPerson.name.split(' ') : []
    const lastName = splitName.length ? splitName[splitName.length - 1] : undefined
    const firstName = splitName.length ? splitName[0] : undefined

    let data = {
      id: id,
      documentNumber: kybPerson?.documentNumber,
      entity: 'PERSON',
      type,
      ...(requiresNameAndLast && {
        name: firstName ?? removeLastWord(kybPerson?.name).modifiedString,
      }),
      ...((requiresOnlyLastName || requiresNameAndLast) && {
        last_name: lastName ?? removeLastWord(kybPerson?.name).lastWord,
      }),
      date_expedition:
        type === 'registraduria-co' || type === 'inhabilidades-co' || type === 'rmnc-co'
          ? kybPerson?.dateOfRegistration ?? '1994-05-03'
          : undefined,
    }

    data = addAdditionalParams(type, data)

    try {
      await createCheck({ countrySegment, body: data }).unwrap()
      toaster.success(t('messages:creationSuccess'))
    } catch (error) {
      console.error('[CREATE NEW COMMENT ERROR] --> ', error?.data?.data)
      toaster.error(error.message ? error.message : t('messages:creationError'))
    } finally {
      handleFetch()
    }
  }

  const postNewComment = async (newComment) => {
    try {
      await createNewComment({
        countrySegment,
        id,
        body: {
          emailClient: emailClient,
          email: email,
          description: newComment,
        },
      }).unwrap()
      toaster.success(t('messages:createCommentSuccess'))
      setModalNewCommentOpen(false)
    } catch (error) {
      console.error('[CREATE NEW COMMENT ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:createCommentError'))
    } finally {
      handleFetch()
    }
  }

  const confirmIgnoreRiskMatrix = async (reason) => {
    try {
      await postNewComment('Ignore Risk Matrix: ' + reason, false)
      await updateKybPerson({
        id,
        countrySegment,
        body: {
          ignoreRiskMatrix: true,
        },
      }).unwrap()

      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      toaster.error(t('messages:updateError'))
      console.error('[UPDATE KYB ERROR] --> ', error?.data?.data)
    } finally {
      setModalIgnoreRiskMatrix(false)
    }
  }

  const handleUpdate = async (body) => {
    if (body.ignoreRiskMatrix) {
      setModalIgnoreRiskMatrix(true)
      return
    }

    try {
      await updateKybPerson({ id, countrySegment, body }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      handleFetch()
    }
  }

  // RISK LEVEL
  const handleUpdateRiskLevel = async (lvl) => {
    try {
      await updateRiskLevel({ countrySegment, id, lvl }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE RISK LEVEL ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setModalEditRiskLevel(false)
    }
  }

  const handleRemoveManualAddress = async () => {
    try {
      await updateKybPerson({ id, countrySegment, body: { addresses: [] } }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setRemoveAddressModalOpen(false)
    }
  }

  const handleRemoveManualEmail = async () => {
    try {
      await updateKybPerson({ id, countrySegment, body: { emailList: [] } }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setRemoveEmailModalOpen(false)
    }
  }

  const handleRemoveManualPhoneNumber = async () => {
    try {
      await updateKybPerson({ id, countrySegment, body: { phoneNumberList: [] } }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setRemovePhoneNumberModalOpen(false)
    }
  }

  const handleEditManualAddress = async (body) => {
    try {
      await updateKybPerson({ id, countrySegment, body }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setEditAddressModalOpen(false)
    }
  }

  const handleEditManualEmail = async (_, value) => {
    const body = {
      emailList: [
        {
          EmailAddress: value,
          type: 'MANUAL',
        },
      ],
    }

    try {
      await updateKybPerson({ id, countrySegment, body }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setEditEmailModalOpen(false)
    }
  }

  const handleEditManualPhoneNumber = async (body) => {
    try {
      await updateKybPerson({ id, countrySegment, body }).unwrap()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      setEditPhoneNumberModalOpen(false)
    }
  }

  const handleRunRiskMatrix = async () => {
    try {
      await runRiskMatrix({ id, countrySegment }).unwrap()
      toaster.success(t('messages:riskMatrixRunSuccess'))
    } catch (error) {
      toaster.error(error?.data?.data ?? t('messages:riskMatrixRunError'))
      console.error('[RUN RISK MATRIX ERROR] --> ', error?.data?.data || error)
    }
  }

  const handleCheckUpdate = async (checkId, data) => {
    const body = addAdditionalParams(checkType, data)
    try {
      await updateCheck({ countrySegment, checkId, body: { ...body, entity: 'PERSON', entityId: id } }).unwrap()
      toaster.success(t('messages:updateSuccess'))
      setCheckId('')
      setCheckStatus('')
      setCheckType('')
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(error.message ? error.message : t('messages:updateError'))
    } finally {
      handleFetch()
    }
  }

  const updateAssigns = (toAssign) => {
    setAssignsToSave(toAssign)
    setModalEditAssign(true)
  }

  const confirmEditAssigns = async (reason) => {
    try {
      await createNewComment({
        countrySegment,
        id,
        body: {
          emailClient,
          email,
          description: 'Edit designated users: ' + reason,
        },
      }).unwrap()

      await updateKybPerson({
        id,
        countrySegment,
        body: {
          assignedUsers: assignsToSave,
          assignedUsersComment: reason,
        },
      }).unwrap()
      toaster.success(t('messages:updateSuccess'))
      setModalEditAssign(false)
    } catch (error) {
      console.error('[UPDATE CASE ASSIGNED USERS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:editAssigUserError'))
    } finally {
      setModalEditAssign(false)
      handleFetch()
    }
  }

  const handleUploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    try {
      await uploadFile({ countrySegment, id, body: formData }).unwrap()
      toaster.success(t('messages:uploadFileSuccess'))
    } catch (error) {
      toaster.error(t('messages:uploadFileError'))
      console.error('[UPLOAD KYB FILE ERROR] --> ', error?.data?.data)
    } finally {
      handleFetch()
    }
  }

  const handleDeleteFile = async (e, file) => {
    e.preventDefault()
    const splittedName = file.split('/kyb/')[1].split('?')
    const key = splittedName[splittedName.length - 2]
    try {
      await deleteFile({ countrySegment, id, key }).unwrap
      toaster.success(t('messages:deletionSuccess'))
    } catch (error) {
      console.error('[DELETE KYB FILE ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:deletionSuccess'))
    }
  }

  const handleIsPrivate = async () => {
    try {
      await updateSubjectIsPrivate({
        country,
        id,
        body: {
          isPrivate: !kybPerson?.isPrivate,
        },
      }).unwrap()
      refetch()
      toaster.success(t('messages:updateSuccess'))
    } catch (error) {
      console.error('[UPDATE STATUS ERROR] --> ', error?.data?.data)
      toaster.error(t('messages:updateError'))
    } finally {
      handleFetch()
    }
  }

  const tabConfig = [
    { value: 'checks', label: t('common:checks') },
    { value: 'certificates', label: t('common:certificates') },
    { value: 'documents', label: t('common:documents') },
    { value: 'relatedPersons', label: t('common:relatedPersons') },
    { value: 'relatedCompanies', label: t('common:relatedCompanies') },
    { value: 'assignedUsers', label: t('common:assignedUsers') },
    { value: 'comments', label: t('common:comments') },
    { value: 'timeline', label: t('common:timelineAndActivity') },
  ]

  const kybMetadata = useMemo(() => {
    if (userMasters?.kybMetadata) {
      return Object.keys(userMasters.kybMetadata).map((key) => ({ name: key, label: userMasters.kybMetadata[key] }))
    }

    return []
  }, [userMasters?.kybMetadata])

  return (
    <Box className={`${(isFetching || isLoading) && 'spinner-transition'} filter`}>
      <Box className="kyb-detail">
        {/* Header Section */}
        <Box className="kyb-detail-head">
          <Paper elevation={0} sx={{ mr: '1rem', padding: '1rem' }}>
            <Box className="kyb-info">
              <Box className="kyb-info-section-head">
                <Box className="kyb-info-section-1">
                  <Typography variant="h4">{kybPerson?.name || '---'}</Typography>
                  {/* Account Type Chip */}
                  <Chip
                    label={t('common:personAccount')}
                    icon={<PersonOutlineOutlinedIcon sx={{ fill: '#fbfffc' }} />}
                    sx={{ backgroundColor: '#00bfe9' }}
                  />
                </Box>
              </Box>
              <Grid container spacing={2}>
                {kybPerson?.documentType && (
                  <Grid xs={4}>
                    <KybTitle
                      name={t('common:documentType')}
                      value={`${kybPerson?.documentType} - ${kybPerson?.documentTypeName}` || '---'}
                    />
                  </Grid>
                )}
                <Grid xs={4}>
                  <KybTitle name={t('common:documentNumber')} value={
                              kybPerson?.documentNumber?.includes('kyb.documentNumber.notFound')
                              ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️`
                              : kybPerson?.documentNumber || '---'} copyable />
                </Grid>
                <Grid xs={4}>
                  <KybTitle
                    name={t('common:nationality')}
                    value={
                      <>
                        {kybPerson?.nationality}
                        {'  '}
                        {!!nationalityIconUrl && <img alt="img" src={nationalityIconUrl} width="16" height="12" />}
                      </>
                    }
                  />
                </Grid>
                {country === 'CR' && (
                  <Grid xs={4}>
                    <KybTitle
                      name={t('common:countryOfBirth')}
                      value={
                        <>
                          {kybPerson?.countryOfBirth}
                          {'  '}
                          {!!nationalityIconUrl && <img alt="img" src={nationalityIconUrl} width="16" height="12" />}
                        </>
                      }
                    />
                  </Grid>
                )}
                {country === 'BR' && (
                  <>
                    <Grid xs={4}>
                      <KybTitle name={t('common:gender')} value={<>{kybPerson?.gender || '---'}</>} />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle name={t('common:motherName')} value={<>{kybPerson?.motherName || '---'}</>} />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle name={t('common:rendaPresumida')} value={<>{kybPerson?.rendaPresumida || '---'}</>} />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('common:situacaoImpostoRenda')}
                        value={<>{kybPerson?.situacaoImpostoRenda || '---'}</>}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('common:cpfRegistrationDate')}
                        value={<>{kybPerson?.cpfRegistrationDate || '---'}</>}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={`${t('common:email')}${manualEmail ? ` (${t('common:manual')})` : ''}`}
                        value={
                          <>
                            {kybPerson?.email || '---'}
                            {kybPerson?.emailList?.length > 1 && (
                              <Box
                                component="span"
                                onClick={() => setEmailModalOpen(true)}
                                sx={{
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  marginLeft: 1,
                                  color: 'primary.main',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  display: 'inline-block',
                                }}
                              >
                                {t('common:seeMore')}
                              </Box>
                            )}
                          </>
                        }
                        editable={country === 'BR'}
                        removable={country === 'BR' && !!manualEmail}
                        onEditClick={() => setEditEmailModalOpen(true)}
                        onRemoveClick={() => setRemoveEmailModalOpen(true)}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={`${t('common:phoneNumber')}${manualPhoneNumber ? ` (${t('common:manual')})` : ''}`}
                        value={
                          <>
                            {kybPerson?.phoneNumber || '---'}
                            {kybPerson?.phoneNumberList?.length > 1 && (
                              <Box
                                component="span"
                                onClick={() => setPhoneNumberModalOpen(true)}
                                sx={{
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  marginLeft: 1,
                                  color: 'primary.main',
                                  display: 'inline',
                                }}
                              >
                                {t('common:seeMore')}
                              </Box>
                            )}
                          </>
                        }
                        editable={country === 'BR'}
                        onEditClick={() => setEditPhoneNumberModalOpen(true)}
                        removable={country === 'BR' && manualPhoneNumber}
                        onRemoveClick={() => setRemovePhoneNumberModalOpen(true)}
                      />
                    </Grid>
                  </>
                )}
                <Grid xs={4}>
                  <KybTitle name={t('common:dateOfBirth')} value={<>{kybPerson?.dateOfBirth || '---'}</>} />
                </Grid>
                {/* Address */}
                {/* Address */}
                {country !== 'CO' && (
                  <Grid xs={4}>
                    <KybTitle
                      name={`${t('common:address')}${manualAddress ? ` (${t('common:manual')})` : ''}`}
                      value={
                        <Typography component="span" variant="caption">
                          {finalAddress || 'Address not found.'}
                          {country === 'BR' && kybPerson?.addresses?.length > 1 && (
                            <Box
                              component="span"
                              onClick={() => setAddressModalOpen(true)}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                marginLeft: 1,
                                color: 'primary.main',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                display: 'inline-block',
                              }}
                            >
                              {t('common:seeMore')}
                            </Box>
                          )}
                        </Typography>
                      }
                      icon={
                        coordinates && (
                          <Box className="kyb-address-icon">
                            <Link
                              href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}&z=16`}
                              target="_blank"
                              variant="contained"
                              color="primary"
                            >
                              <Typography variant="number">{t('common:openMap')}</Typography>
                            </Link>
                          </Box>
                        )
                      }
                      editable={country === 'BR'}
                      onEditClick={() => setEditAddressModalOpen(true)}
                      removable={country === 'BR' && manualAddress?.type === 'MANUAL'}
                      onRemoveClick={() => setRemoveAddressModalOpen(true)}
                    />
                  </Grid>
                )}
                {kybPerson?.isPep !== undefined && (
                  <Grid xs={2}>
                    <KybTitle name={t('common:isPep')} value={<>{kybPerson?.isPep ? 'Si' : 'No'}</>} />
                  </Grid>
                )}
                {kybPerson?.isReportingEntity !== undefined && (
                  <Grid xs={2}>
                    <KybTitle
                      name={t('common:isReportingEntity')}
                      value={<>{kybPerson?.isReportingEntity ? t('common:yes') : t('common:no')}</>}
                    />
                  </Grid>
                )}
                {country === 'CR' && (
                  <>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('messages:kyb.jobDetails.professionName')}
                        value={kybPerson?.jobDetails?.professionName || '---'}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('messages:kyb.jobDetails.ocupationName')}
                        value={kybPerson?.jobDetails?.ocupationName || '---'}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('messages:kyb.jobDetails.jobStatusName')}
                        value={kybPerson?.jobDetails?.jobStatusName || '---'}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('messages:kyb.jobDetails.jobBusinessActivityName')}
                        value={kybPerson?.jobDetails?.jobBusinessActivityName || '---'}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle name={t('messages:kyb.income')} value={kybPerson?.income?.name || '---'} />
                    </Grid>
                    <Grid xs={4}>
                      <KybTitle
                        name={t('messages:kyb.transactionalAmount')}
                        value={kybPerson?.transactionalAmount?.name || '---'}
                      />
                    </Grid>
                  </>
                )}
                {userMasters?.kybMonitoringEnable && (
                  <>
                    <Grid xs={2}>
                      <KybTitle
                        name={t('common:monitored')}
                        value={<>{kybPerson?.monitored ? t('common:yes') : t('common:no')}</>}
                      />
                    </Grid>
                    <Grid xs={2}>
                      <KybTitle
                        name={t('common:lastResetDate')}
                        value={<>{buildTimeStringFromTimestamp(kybPerson?.lastResetDate)}</>}
                      />
                    </Grid>
                  </>
                )}
                <Grid xs={4}>
                  <KybTitle
                    name={t('common:createdAt')}
                    value={kybPerson?.createdAt ? buildTimeStringFromTimestamp(kybPerson.createdAt) : '---'}
                  />
                </Grid>
                <Grid xs={4}>
                  <KybTitle
                    name={t('common:lastUpdated')}
                    value={kybPerson?.updatedAt ? buildTimeStringFromTimestamp(kybPerson.updatedAt) : '---'}
                  />
                </Grid>
                {kybMetadata?.map(({ name, label }) => (
                  <Grid xs={4} key={name}>
                    <KybTitle
                      name={label}
                      value={
                        kybPerson?.metadata && Object.prototype.hasOwnProperty.call(kybPerson.metadata, name)
                          ? kybPerson.metadata[name]
                          : '---'
                      }
                      // TO-DO: Uncomment when metadata editing is enabled
                      // onEditClick={() =>
                      //   handleOpenModalEditField({
                      //     label,
                      //     name: `metadata.${name}`,
                      //     value:
                      //       kybBusiness?.metadata && Object.prototype.hasOwnProperty.call(kybBusiness.metadata, name)
                      //         ? kybBusiness.metadata[name]
                      //         : '',
                      //   })
                      // }
                      // editable={true}
                      // removable={Object.prototype.hasOwnProperty.call(kybBusiness?.metadata || {}, name)}
                      // onRemoveClick={() => handleUpdateKyb('metadata', name, { deleteMetadataKey: true })}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
          <Box className="details-sub">
            {roles.includes('MANAGER') && (
              <Box display="flex" alignItems="center" gap="1rem" justifyContent="flex-end">
                {/* TO-DO: Uncomment when monitoring is enabled */}
                {/* <Box display="flex" alignItems="center" gap="0.5rem">
                  <Switch
                    checked={kybPerson?.monitored}
                    onChange={handleMonitored}
                    disabled={!userMasters?.kybMonitoringEnable}
                  />
                  <Typography variant="subtitle2">{t('common:monitored')}</Typography>
                </Box> */}
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <Switch
                    checked={kybPerson?.isPrivate || false}
                    onChange={handleIsPrivate}
                    disabled={!activeServices.includes('kyb:idprivate')}
                  />
                  <Typography variant="subtitle2">{t('common:private')}</Typography>
                </Box>
              </Box>
            )}
            <Button
              disabled={!activeServices.includes('kyb:idcomments')}
              sx={{ width: '100%' }}
              size="small"
              onClick={() => setModalNewCommentOpen(true)}
              variant="contained"
            >
              {t('common:postComment')}
            </Button>
            {country !== 'CO' && (
              <Button
                sx={{ width: '100%' }}
                size="small"
                onClick={() => setOpenModalKybReset(true)}
                disabled={!activeServices.includes('kyb:idrestart')}
                variant="outlined"
              >
                {t('common:reset')}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setShowDownloadModal(true)}
              endIcon={<IosShareOutlinedIcon sx={{ fill: 'var(--lime-04)!important' }} />}
              disabled={!activeServices.includes('kybv2:country')}
            >
              <Typography variant="title2">{t('common:exportReport')}</Typography>
            </Button>
            {kycId && (
              <Button
                sx={{ width: '100%' }}
                size="small"
                onClick={() => window.open(`/kyc/person/${kycId}`, '_blank')}
                disabled={!activeServices.includes('kybv2:country')}
                variant="outlined"
              >
                {t('common:SEE_KYC_IDENTITY_VERIFICATION')}
              </Button>
            )}
            {kycV2Id && (
              <Button
                sx={{ width: '100%' }}
                size="small"
                onClick={() => window.open(`/kycv2/person/${kycV2Id}`, '_blank')}
                disabled={!activeServices.includes('kybv2:country')}
                variant="outlined"
              >
                {t('common:SEE_KYC_IDENTITY_VERIFICATION')} v2
              </Button>
            )}
            {kytId && (
              <Button
                sx={{ width: '100%' }}
                size="small"
                onClick={() => window.open(`/kyt/users/consumer/${kytId}`, '_blank')}
                variant="outlined"
              >
                {t('common:monitoringProfile')}
              </Button>
            )}
            {country !== 'CO' && (
              <Paper elevation={0} sx={{ p: '1rem' }} className="risk-level">
                <Box className="risk-level-top">
                  <Typography variant="subtitle2" style={{ minHeight: '2rem' }}>
                    {t('common:riskLevel')}
                  </Typography>
                  {kybPerson?.ignoreRiskMatrix ? (
                    <Button
                      size="small"
                      onClick={() => setModalEditRiskLevel(true)}
                      variant="outlined"
                      disabled={!activeServices.includes('kyb:idriskLevel')}
                    >
                      {t('common:edit')}
                    </Button>
                  ) : (
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      onClick={handleRunRiskMatrix}
                      loading={isRunningRiskMatrix}
                      disabled={!activeServices.includes('kyb:idriskMatrix')}
                    >
                      {t('common:run')}
                    </LoadingButton>
                  )}
                </Box>
                {kybPerson?.riskLevel ? (
                  <RiskLevelSlider
                    type={kybPerson?.riskLevel}
                    onShowDetail={() => setOpenModalRiskResult(true)}
                    showDetail={!kybPerson?.ignoreRiskMatrix && !!kybPerson?.riskResult}
                  />
                ) : (
                  <RiskLevelSlider type={'LOW'} />
                )}
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        inputProps={{ 'aria-label': 'controlled' }}
                        checked={kybPerson?.ignoreRiskMatrix ?? false}
                        onChange={() => handleUpdate({ ignoreRiskMatrix: !kybPerson?.ignoreRiskMatrix })}
                        disabled={!activeServices.includes('kyb:id')}
                      />
                    }
                    label={t('messages:kyb.ignoreRiskMatrix')}
                  />
                </Box>
              </Paper>
            )}
            <Paper elevation={0} sx={{ p: '1rem' }} className="status-level">
              <Box className="status-level-top">
                <Typography variant="subtitle2">{t('common:status')}</Typography>
                <Button
                  size="small"
                  onClick={() => setEditStatusModal(true)}
                  disabled={!activeServices.includes('kybsubject:id')}
                  variant="outlined"
                >
                  {t('common:edit')}
                </Button>
              </Box>
              {kybPerson?.status ? (
                <StatusIndicator type={kybPerson.status} color={colorsByStatus[kybPerson.status]} />
              ) : (
                <StatusIndicator type={'NOT_STARTED'} />
              )}
            </Paper>
          </Box>
        </Box>
        <Box className="kyb-subgrid">
          <TabContext value={selectedTab}>
            <CustomTabList
              TabIndicatorProps={{ style: { display: 'none' } }}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
            >
              {tabConfig.map((tab) => (
                <Tab
                  key={tab.value}
                  label={
                    <Chip
                      label={tab.label}
                      sx={{ fontSize: 16 }}
                      variant={selectedTab === tab.value ? 'sliderBlack' : 'sliderWhite'}
                    />
                  }
                  value={tab.value}
                  sx={{ maxWidth: '100%' }}
                />
              ))}
            </CustomTabList>
            <Box>
              <TabPanel value="checks">
                <Box className="kyb-checks-flex">
                  {checks?.map((mainChecks, indexMain) => (
                    <Accordion
                      key={indexMain}
                      expanded={expandedMain[indexMain]}
                      onChange={() => handleChangeExpandedMain(indexMain)}
                    >
                      <AccordionSummary expandIcon={null}>
                        <Box className="accordion-wrapper">
                          <Box className="accordion-status">
                            <Typography variant="title">
                              {t(`messages:kyb.checks.title.${mainChecks?.title}`)}
                            </Typography>
                            <Typography variant="subtitle3">
                              {t(`messages:kyb.checks.subtitle.${mainChecks?.subtitle}`)}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlinedBlank"
                            onChange={(e) => {
                              e.stopPropagation()
                              return handleChangeExpandedMain(indexMain)
                            }}
                          >
                            {expandedMain[indexMain] ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
                          </Button>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {mainChecks?.items?.map((check, index) => {
                          const key = `${indexMain}-${index}`
                          return (
                            <Accordion
                              key={key}
                              expanded={expanded[key] || false}
                              onChange={(e) => {
                                e.stopPropagation()
                                handleChangeExpanded(indexMain, index)
                              }}
                              variant="medium"
                              slotProps={{ transition: { unmountOnExit: true } }}
                            >
                              <AccordionSummary expandIcon={null}>
                                <Box className="accordion-wrapper">
                                  <Box className="accordion-status">
                                    <Typography variant="text2">{t(`common:${check.title}`)}</Typography>
                                    <Box>
                                      {kybPerson?.[check?.name] ? (
                                        <Box className="accordion-status-icon">
                                          <Status
                                            type={kybPerson[check.name].status}
                                            noIcon={true}
                                            sizeBig={true}
                                            variant="colorSecondary"
                                          />
                                        </Box>
                                      ) : (
                                        <Box className="accordion-status-icon">
                                          <Status
                                            type={'NOT_STARTED'}
                                            noIcon={true}
                                            sizeBig={true}
                                            variant="colorSecondary"
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  <Button
                                    variant="outlinedBlank"
                                    onChange={(e) => {
                                      e.stopPropagation()
                                      return handleChangeExpanded(index)
                                    }}
                                  >
                                    {expanded[index] ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
                                  </Button>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box>
                                  {kybPerson?.[check?.name] ? (
                                    <KybCheckDetail name={check.name} value={kybPerson[check.name]} type="PERSON" />
                                  ) : (
                                    <Tooltip width="100%" title="">
                                      <span style={{ display: 'inline-block', width: '100%' }}>
                                        <LoadingButton
                                          size="small"
                                          fullWidth
                                          variant="contained"
                                          color="secondary"
                                          loading={!!createCheckLoading && createCheckArgs?.body?.type === check.key}
                                          onClick={(e) => handleCreateCheck(e, check.key)}
                                          disabled={!activeServices.includes('kybcheck')}
                                        >
                                          {t('common:run')} {KybUtils.getShortCheckLabel(t(`common:${check.title}`))}
                                        </LoadingButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  <Box className="kyb-close">
                                    {kybPerson?.[check?.name] && (
                                      <Box className="kyb-check-status">
                                        <KybPreviousChecks
                                          kybId={id}
                                          type={check.key}
                                          checks={checks}
                                          countrySegment={countrySegment}
                                        />
                                        <Button
                                          size="small"
                                          fullWidth
                                          onClick={() => {
                                            setOpenModalCheckStatus(!openModalCheckStatus)
                                            setCheckId(kybPerson[check.name]?._id)
                                            setCheckStatus(kybPerson[check.name]?.status)
                                            setCheckType(check.key)
                                          }}
                                          disabled={!activeServices.includes('kybcheck:checkId')}
                                          variant="contained"
                                        >
                                          {t('common:changeStatus')}
                                        </Button>
                                        <LoadingButton
                                          size="small"
                                          fullWidth
                                          variant="outlined"
                                          loading={!!createCheckLoading && createCheckArgs?.body?.type === check.key}
                                          onClick={(e) => handleCreateCheck(e, check.key)}
                                          disabled={!activeServices.includes('kybcheck')}
                                        >
                                          {t('common:run')} {KybUtils.getShortCheckLabel(t(`common:${check.title}`))} {t('common:again')}
                                        </LoadingButton>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          )
                        })}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </TabPanel>
              <TabPanel value="certificates">
                <CNJNegativeCertificate
                  countrySegment={countrySegment}
                  entityType="PERSON"
                  handleFetch={handleFetch}
                  kyb={kybPerson}
                  setOpenModalCheckStatus={setOpenModalCheckStatus}
                  openModalCheckStatus={openModalCheckStatus}
                  setCheckId={setCheckId}
                  setCheckStatus={setCheckStatus}
                  setCheckType={setCheckType}
                  checks={checks}
                  currentCountry={currentCountry}
                />
              </TabPanel>
              <TabPanel value="documents">
                <Paper elevation={0}>
                  <Box className="kyb-detail-documents">
                    <Box className="kyb-documents-top">
                      <Typography variant="title">{t('common:documents')}</Typography>
                      <Typography variant="subtitle3">{t('messages:updatePDFRelated')}</Typography>
                    </Box>
                    <Box className="kyb-files-drag">
                      <Typography variant="title2">{t('common:addAFile')}</Typography>
                      <FileUploader
                        handleChange={(file) => handleUploadFile(file)}
                        name="file"
                        label={t('common:fileUploaderLabel')}
                        types={['JPG', 'JPEG', 'PNG', 'GIF', 'PDF', 'DOC', 'CSV']}
                      >
                        <Paper elevation={0} variant="rootDashedTransparent" className="draggable-main">
                          <Box className="draggable-icon"></Box>
                          <Typography variant="title2">{t('common:fileUploaderLabel')}</Typography>
                          <Box>
                            <Typography variant="subtitle4">{t('common:or')} </Typography>
                            <Typography sx={{ textDecoration: 'underline' }} variant="subtitle4" color="secondary">
                              {t('common:browseMedia')}
                            </Typography>
                          </Box>
                        </Paper>
                      </FileUploader>
                      <Typography variant="subtitle4">
                        {t('common:supportedMedia')}: JPG, PNG, GIF, MP4, MPEG, WEBM, MP3, WAV, PDF.
                      </Typography>
                    </Box>
                    <Box className="kyb-file-list-wrapper">
                      <Typography variant="title"> {t('common:uploadedDocuments')}</Typography>
                      <Box className="kyb-file-list">
                        {isProcessingFile || isProcessingFileDelete ? (
                          <ProcessingSpinner message={t('common:processing')} />
                        ) : (
                          <Box>
                            {Array.isArray(kybPerson?.files) && kybPerson.files.length > 0 && (
                              <Box className="kyb-files">
                                {typeof kybPerson?.files[0] === 'string' &&
                                  kybPerson.files.map((file, index) => {
                                    const splittedName = file.split('/kyb/')[1].split('_')
                                    const fileName = splittedName[splittedName.length - 2]?.replaceAll('%20', ' ')
                                    return (
                                      <Paper elevation={0} variant="colorPrimary" className="kyb-file" key={index}>
                                        <Box className="kyb-file-description">
                                          <Box className="color-button">
                                            <Typography variant="subtitle1" sx={{ color: '#FF2B1B' }}>
                                              DOC
                                            </Typography>
                                          </Box>
                                          <Typography variant="text2" title={fileName}>
                                            {fileName}
                                          </Typography>
                                        </Box>
                                        <Box className="kyb-file-icons">
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => window.open(file, '_blank')}
                                          >
                                            {t('common:view')}
                                          </Button>
                                          <Button variant="outlinedBlank" onClick={(e) => handleDeleteFile(e, file)}>
                                            <DeleteOutlineIcon />
                                          </Button>
                                        </Box>
                                      </Paper>
                                    )
                                  })}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </TabPanel>
              <TabPanel value="relatedPersons">
                <Paper elevation={0}>
                  <TabComponent
                    entityType={'PERSON'}
                    subjectType="relatedPerson"
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    documentNumber={kybPerson?.documentNumber}
                    handleFetch={handleFetch}
                  />
                </Paper>
              </TabPanel>
              <TabPanel value="relatedCompanies">
                <Paper elevation={0}>
                  <TabComponent
                    entityType={'PERSON'}
                    subjectType="relatedCompany"
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    documentNumber={kybPerson?.documentNumber}
                    handleFetch={handleFetch}
                    relationships={kybPerson?.relationships}
                  />
                </Paper>
              </TabPanel>
              <TabPanel value="assignedUsers">
                <Paper elevation={0}>
                  <Box className="kyb-card">
                    <Box className="card-top">
                      <Typography variant="title">{t('common:assignedUsers')}</Typography>
                      {!isEditingAssign ? (
                        <Button
                          disabled={!activeServices.includes('kybsubject:id')}
                          onClick={() => {
                            setIsEditingAssign(true)
                          }}
                          size="small"
                          variant="contained"
                        >
                          {t('common:editUsers')}
                        </Button>
                      ) : (
                        <Fab variant="close" onClick={() => setIsEditingAssign(false)}>
                          <CloseOutlinedIcon />
                        </Fab>
                      )}
                    </Box>
                    <DatatableAssignUsers
                      rows={kybPerson?.assignedUsers || []}
                      toAssign={availableUsers}
                      isEditing={isEditingAssign}
                      updateAssigns={updateAssigns}
                      setIsEditingAssign={setIsEditingAssign}
                    />
                  </Box>
                </Paper>
              </TabPanel>
              <TabPanel value="comments">
                <Paper elevation={0}>
                  <Box className="kyb-card">
                    <Box className="card-top">
                      <Typography variant="title">{t('common:comments')}</Typography>
                    </Box>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('common:email')}</TableCell>
                            <TableCell>{t('common:createdAt')}</TableCell>
                            <TableCell>{t('common:comment')}</TableCell>
                          </TableRow>
                        </TableHead>
                        {Array.isArray(kybPerson?.comments) && kybPerson?.comments?.length > 0 ? (
                          kybPerson.comments.map((row, index) => (
                            <TableBody key={index}>
                              <TableRow>
                                <TableCell>
                                  <Box>{row.email ?? '---'}</Box>
                                </TableCell>
                                <TableCell>
                                  {row.createdAt ? buildTimeStringFromTimestamp(row.createdAt) : '---'}
                                </TableCell>
                                <TableCell>{row.description ?? '---'}</TableCell>
                              </TableRow>
                            </TableBody>
                          ))
                        ) : (
                          <Box className="table-custom-overlay">
                            <CustomNoRowsOverlay />
                          </Box>
                        )}
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              </TabPanel>
              <TabPanel value="timeline">
                <Paper elevation={0}>
                  <TimelineTable id={id} country={currentCountry} target="person" />
                </Paper>
              </TabPanel>
            </Box>
          </TabContext>
        </Box>

        <Modal
          open={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box className="modal">
            <KybToPdf
              kyb={kybPerson}
              entity={'PERSON'}
              currentCountry={currentCountry}
              setOpen={setShowDownloadModal}
            />
          </Box>
        </Modal>

        <KybModal
          title={t('common:postComment')}
          subtitle={t('messages:commentDesc')}
          open={modalNewCommentOpen}
          setOpen={setModalNewCommentOpen}
        >
          <KybNewCommentModal onSubmit={postNewComment} />
        </KybModal>

        <KybModal
          title={`${t('common:edit')} KYB ${t('common:status')}`}
          open={editStatusModal}
          setOpen={setEditStatusModal}
        >
          <KybEditStatusModal defaultValue={kybPerson?.status || 'NOT_STARTED'} onSubmit={handleUpdate} />
        </KybModal>

        <KybModal
          title={`${t('common:edit')} ${t('common:check')} ${t('common:status')}`}
          open={openModalCheckStatus}
          setOpen={setOpenModalCheckStatus}
        >
          <KybEditCheckStatusModal defaultValue={checkStatus} checkId={checkId} onSubmit={handleCheckUpdate} />
        </KybModal>
        <KybModal title={t('messages:reset.kyb.modal.title')} open={openModalKybReset} setOpen={setOpenModalKybReset}>
          <KybResetModal onSubmit={handleRestart} mutationKey="subject-reset" />
        </KybModal>
        <KybModal title={t('common:addresses')} open={addressModalOpen} setOpen={setAddressModalOpen}>
          <Box p={2} maxHeight="70vh" overflow="auto">
            {allAddresses.map((addr, idx) => (
              <Box key={idx} mb={2}>
                <Typography fontWeight="bold">{t(`common:${addr.label}`)}</Typography>
                <Typography>{addr.formatted}</Typography>
                {addr.passageDate && (
                  <Typography variant="body2" color="text.secondary">
                    {t('common:lastPassageDate')}: {addr.passageDate}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </KybModal>
        <KybModal title={t('common:emailList')} open={emailModalOpen} setOpen={setEmailModalOpen}>
          <Box p={2} maxHeight="70vh" overflow="auto">
            {emailList.map((emailDetail, idx) => (
              <Box key={idx} mb={2}>
                <Typography fontWeight="bold">{emailDetail.label}</Typography>
                <Typography>{emailDetail.email}</Typography>
              </Box>
            ))}
          </Box>
        </KybModal>
        <KybModal title={t('common:phoneNumberList')} open={phoneNumberModalOpen} setOpen={setPhoneNumberModalOpen}>
          <Box p={2} maxHeight="70vh" overflow="auto">
            {phoneNumberList.map((phoneNumberDetail, idx) => (
              <Box key={idx} mb={2}>
                <Typography fontWeight="bold">{phoneNumberDetail.label}</Typography>
                <Typography>{phoneNumberDetail.phoneNumber}</Typography>
                {phoneNumberDetail.passageDate && (
                  <Typography variant="body2" color="text.secondary">
                    {t('common:lastPassageDate')}:{phoneNumberDetail.passageDate}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </KybModal>

        <Modal
          open={openModalRiskResult}
          onClose={() => setOpenModalRiskResult(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <RiskMatrixResultModal
            country={country}
            riskHistory={riskHistory}
            handleFetchHistory={handleFetchHistory}
            handleFetchLastHistory={handleFetchLastHistory}
            prev={riskHistory?.prev}
            nextnext={riskHistory?.next}
            setModalOpen={setOpenModalRiskResult}
          />
        </Modal>

        <KybModal
          title={`${t('common:edit')} ${t('common:address')}`}
          open={editAddressModalOpen}
          setOpen={setEditAddressModalOpen}
        >
          <KybPersonEditManualAddress initialValue={manualAddress} onSubmit={handleEditManualAddress} />
        </KybModal>

        <KybModal
          title={`${t('common:edit')} ${t('common:email')}`}
          open={editEmailModalOpen}
          setOpen={setEditEmailModalOpen}
        >
          <KybEditFieldModal name={'email'} initialValue={manualEmail?.EmailAddress} onSubmit={handleEditManualEmail} />
        </KybModal>

        <KybModal
          title={`${t('common:delete')} ${t('common:address')}`}
          open={removeAddressModalOpen}
          setOpen={setRemoveAddressModalOpen}
        >
          <KybPersonRemoveManualAddress
            initialValue={finalAddress}
            onConfirm={() => handleRemoveManualAddress()}
            onCancel={() => setRemoveAddressModalOpen(false)}
          />
        </KybModal>

        <KybModal
          title={`${t('common:delete')} ${t('common:email')}`}
          open={removeEmailModalOpen}
          setOpen={setRemoveEmailModalOpen}
        >
          <KybPersonRemoveManualEmail
            initialValue={finalEmail}
            onConfirm={() => handleRemoveManualEmail()}
            onCancel={() => setRemoveEmailModalOpen(false)}
          />
        </KybModal>

        <KybModal
          title={`${t('common:edit')} ${t('common:phoneNumber')}`}
          open={editPhoneNumberModalOpen}
          setOpen={setEditPhoneNumberModalOpen}
        >
          <KybPersonEditManualPhoneNumber initialValue={manualPhoneNumber} onSubmit={handleEditManualPhoneNumber} />
        </KybModal>

        <KybModal
          title={`${t('common:delete')} ${t('common:phoneNumber')}`}
          open={removePhoneNumberModalOpen}
          setOpen={setRemovePhoneNumberModalOpen}
        >
          <KybPersonRemoveManualPhoneNumber
            initialValue={kybPerson?.phoneNumber}
            onConfirm={() => handleRemoveManualPhoneNumber()}
            onCancel={() => setRemovePhoneNumberModalOpen(false)}
          />
        </KybModal>

        <KybModal title="" open={modalIgnoreRiskMatrix} setOpen={setModalIgnoreRiskMatrix}>
          <KybIgnoreRiskMatrixReason onSubmit={confirmIgnoreRiskMatrix} isLoading={createNewCommentLoading} />
        </KybModal>

        <KybModal title={t('common:editUsers')} open={modalEditAssign} setOpen={setModalEditAssign}>
          <KybEditAssignedUsersReason onSubmit={confirmEditAssigns} isLoading={createNewCommentLoading} />
        </KybModal>

        <KybModal
          title={`${t('common:edit')} KYB ${t('common:riskLevel')}`}
          open={modalEditRiskLevel}
          setOpen={setModalEditRiskLevel}
        >
          <KybEditRiskLevelModal initialValue={kybPerson?.riskLevel || 'LOW'} onSubmit={handleUpdateRiskLevel} />
        </KybModal>
      </Box>
    </Box>
  )
}

export default KybDetailPerson
